import { useState } from "react";
import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { AnimatedExplainer } from "@/components/AnimatedExplainer";
import { Insight } from "@/components/Insight";
import { Keyboard } from "@/components/Keyboard";
import { RhythmGrid, RhythmCell } from "@/components/RhythmGrid";
import {
  Tip, KeyIdea, Pull, Steps, Step, NextUp, Compare,
  KeyHighlights, Highlight,
} from "@/components/Callouts";

const blockChords: RhythmCell[] = [
  { hand: "L", on: true, pitches: ["C3"], caption: "C bass" },
  { hand: "R", on: true, pitches: ["C4", "Eb4", "G4"], caption: "Cm" },
  { hand: "L", on: true, pitches: ["Bb2"], caption: "B♭ bass" },
  { hand: "R", on: true, pitches: ["C4", "Eb4", "G4"], caption: "Cm" },
  { hand: "L", on: true, pitches: ["Ab2"], caption: "A♭ bass" },
  { hand: "R", on: true, pitches: ["C4", "Eb4", "G4"], caption: "Cm" },
  { hand: "L", on: true, pitches: ["G2"], caption: "G bass" },
  { hand: "R", on: true, pitches: ["C4", "Eb4", "G4"], caption: "Cm" },
];

// Same bar as blockChords — one bar of 4/4, one bass note per beat — but the right
// hand breaks the chord into C-E♭-G instead of stamping it. Both views must walk the
// full C → B♭ → A♭ → G, or switching to the arpeggio silently drops half the song.
const arpeggio: RhythmCell[] = [
  { hand: "L", on: true, pitches: ["C3"], caption: "C bass" },
  { hand: "R", on: true, pitches: ["C4"], caption: "C" },
  { hand: "R", on: true, pitches: ["Eb4"], caption: "E♭" },
  { hand: "R", on: true, pitches: ["G4"], caption: "G" },
  { hand: "L", on: true, pitches: ["Bb2"], caption: "B♭ bass" },
  { hand: "R", on: true, pitches: ["C4"], caption: "C" },
  { hand: "R", on: true, pitches: ["Eb4"], caption: "E♭" },
  { hand: "R", on: true, pitches: ["G4"], caption: "G" },
  { hand: "L", on: true, pitches: ["Ab2"], caption: "A♭ bass" },
  { hand: "R", on: true, pitches: ["C4"], caption: "C" },
  { hand: "R", on: true, pitches: ["Eb4"], caption: "E♭" },
  { hand: "R", on: true, pitches: ["G4"], caption: "G" },
  { hand: "L", on: true, pitches: ["G2"], caption: "G bass" },
  { hand: "R", on: true, pitches: ["C4"], caption: "C" },
  { hand: "R", on: true, pitches: ["Eb4"], caption: "E♭" },
  { hand: "R", on: true, pitches: ["G4"], caption: "G" },
];

export default function PianoFoundation11() {
  const { lesson, course, accent } = findLesson("piano-foundation", "11");
  const [mode, setMode] = useState<"blocks" | "arp">("blocks");

  return (
    <LessonShell
      course={course}
      courseSlug="piano-foundation"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <AnimatedExplainer
        src="/animations/piano-foundation/PF11-Hit-Road-Jack-Animated.mp4"
        caption={`LH legato bass against RH staccato Am — the Oom-Pah engine in motion`}
        hint={`≈ 24 sec · two articulations, one groove`}
      />
      <KeyHighlights>
        <Highlight>Right hand parks on a single chord — <strong>C minor (C, E♭, G)</strong> — for the entire song. Fingers don't matter; pick whatever's comfortable.</Highlight>
        <Highlight>Left hand walks a four-note descending bass: <strong>C → B♭ → A♭ → G</strong>. Then back to C, on repeat.</Highlight>
        <Highlight>The two hands <strong>march</strong>: left, right, left, right. The mnemonic — say <em>"um-pa, um-pa"</em>. UM is the LH bass; PA is the RH chord.</Highlight>
        <Highlight>Left hand <strong>legato</strong> (long, smooth). Right hand <strong>staccato</strong> (short, choppy). Same energy split as a kick-and-snare on a drum kit.</Highlight>
        <Highlight>Sing along: "Hit the road Jack, don't you come back, no more, no more, no more, no more." If the melody bores you, sing the bass walk instead.</Highlight>
      </KeyHighlights>

      <Insight eyebrow="The setup" title="One chord, four bass notes, all night long" accent={accent.hex}>
        <p>
          This is another chord exercise built like the fifth-chord drill from the previous
          lesson — but now we're using a real song. The right hand holds a{" "}
          <strong>C minor chord</strong> (C, E♭, G) and stays there. The left hand walks
          downward through four bass notes: <strong>C → B♭ → A♭ → G</strong>. That's the
          entire harmonic motion.
        </p>

        <Keyboard
          startOctave={2}
          octaves={3}
          highlights={[
            { pitch: "C3", color: "#ef4444", label: "C bass" },
            { pitch: "Bb2", color: "#f97316", label: "B♭" },
            { pitch: "Ab2", color: "#eab308", label: "A♭" },
            { pitch: "G2", color: "#22c55e", label: "G" },
            { pitch: "C4", color: "#3b82f6", label: "Cm" },
            { pitch: "Eb4", color: "#3b82f6", label: "E♭" },
            { pitch: "G4", color: "#3b82f6", label: "G" },
          ]}
          height={180}
        />

        <p className="prose-nsm text-sm">
          The four warm-coloured keys on the left are your descending bass walk. The three
          blue keys are the C minor chord that never moves.
        </p>
      </Insight>

      <Insight eyebrow="The march" title="Left, right, left, right · um-pa, um-pa" accent={accent.hex}>
        <Pull>
          Um is going to be the left hand and pa will be the chord in the right hand.
        </Pull>

        <p>
          The two hands alternate exactly like a marching pattern. Your left hand stamps the
          bass, then your right hand stamps the chord, then back to the left, and so on. The
          "um-pa" phrase is the ideal counting tool — it stays in your mouth while your hands
          do the work.
        </p>

        <Compare
          leftLabel="Left hand · LEGATO"
          rightLabel="Right hand · STACCATO"
          left={
            <ul className="space-y-1 text-[13px]">
              <li>Smoother, longer notes</li>
              <li>Hold each bass note until the next one lands</li>
              <li>Acts like an upright bass</li>
            </ul>
          }
          right={
            <ul className="space-y-1 text-[13px]">
              <li>Choppy, short hits</li>
              <li>Release immediately — bounce off the keys</li>
              <li>Acts like a snare drum</li>
            </ul>
          }
        />

        <KeyIdea>
          The dynamic contrast is what gives this groove its life. Long bass + short chord =
          a marching feel. Both legato or both staccato kill it.
        </KeyIdea>
      </Insight>

      <Insight eyebrow="Hear it · two textures" title="Blocks first · then arpeggio" accent={accent.hex}>
        <div className="mb-3 flex items-center gap-2">
          <button
            onClick={() => setMode("blocks")}
            className={mode === "blocks" ? "nsm-btn-primary" : "nsm-btn-ghost"}
          >
            Block chords (the march)
          </button>
          <button
            onClick={() => setMode("arp")}
            className={mode === "arp" ? "nsm-btn-primary" : "nsm-btn-ghost"}
          >
            Arpeggio variation
          </button>
        </div>

        {mode === "blocks" ? (
          <RhythmGrid
            cells={blockChords}
            subdivisions={2}
            beats={4}
            bpm={92}
            label="Um-pa, um-pa, um-pa, um-pa — bass walks C → B♭ → A♭ → G"
          />
        ) : (
          <RhythmGrid
            cells={arpeggio}
            subdivisions={4}
            beats={4}
            bpm={92}
            label="Arpeggio — left, right, right, right (C, E♭, G one at a time) · same bass walk"
          />
        )}

        <p className="prose-nsm text-sm">
          The arpeggio variation breaks the C minor chord into single notes — <strong>C, E♭, G</strong> —
          played one after the other instead of all at once. The pattern is now <em>left, right,
          right, right</em>. The bass still walks the same four notes underneath.
        </p>
      </Insight>

      <Insight eyebrow="Add octaves if you can" title="Optional thickening for the left hand" accent={accent.hex}>
        <p>
          For more weight in the bass, double each LH note with its octave above (so the
          C bass becomes a C-C two-note grab, B♭ becomes B♭-B♭, and so on). Use whichever
          fingers feel right — no specific fingering is prescribed for any particular fingering for this
          drill.
        </p>

        <Tip>
          If your hand can't reach the octave comfortably, <strong>just play single notes</strong>.
          A clean single bass note in time always beats a strained octave that drags behind
          the beat.
        </Tip>
      </Insight>

      <Insight eyebrow="Sing the song" title="The lyric, the bass, or both" accent={accent.hex}>
        <p>
          Singing while you play is what turns the drill into music. The chorus lyric:
        </p>

        <p className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-3 font-display text-[15px] text-amber-200">
          "Hit the road, Jack, don't you come back, no more, no more, no more, no more."
        </p>

        <p>
          If the melody starts to bore you, switch to singing the <strong>bass</strong>{" "}
          instead — sing C, B♭, A♭, G in time with the LH walk. The drill stays alive even
          without the lead vocal.
        </p>

        <Pull>
          It's important for you to listen to the original song by Ray Charles himself.
          So, head over and make sure you also listen to the song.
        </Pull>
      </Insight>

      <Insight eyebrow="Arrange the song" title="Arpeggio for the verse, blocks for the chorus" accent={accent.hex}>
        <p>
          The two textures fit together as <strong>two parts of a song</strong>. The lesson suggests using the <em>arpeggio</em> as the verse (A-section) and the
          <em> block chords</em> as the chorus (B-section). Same chord, same bass walk —
          completely different feel.
        </p>

        <Steps>
          <Step>Verse — arpeggio. C-E♭-G one note at a time over the four bass notes. Sing softly.</Step>
          <Step>Chorus — back to blocks. Right hand stamps Cm on every "pa". Sing the lyric out.</Step>
          <Step>Verse 2 — arpeggio again. Drop the energy. Maybe slower.</Step>
          <Step>Final chorus — blocks, full volume, both hands committed.</Step>
        </Steps>
      </Insight>

      <NextUp
        to="/lesson/piano-foundation-12"
        label="Lesson 12 · Left Hand Patterns (Root + Octave) - PART 1"
        why="The left hand has been doing one job — the bass walk. The next lesson teaches it four reusable rhythm patterns that work under any chord change."
      />
    </LessonShell>
  );
}
